import React, {
  FC,
  SetStateAction,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react"

import {
  Autocomplete,
  Box,
  Button,
  TextField as TextFieldMui,
  Typography,
  Stack,
} from "@mui/material"
import { useTranslation } from "react-i18next"
import { FixedSizeList, ListChildComponentProps } from "react-window"
import { scrollbarStyles } from "themes/scrollbar"
import { IStudentCandidate } from "ts/interfaces/Student"

interface IProps {
  onChange: (value: IStudentCandidate | null) => void
  onAddNewStudentClick: () => void
  isLoading: boolean
  isError: boolean
  options: IStudentCandidate[]
  setIsChange: React.Dispatch<SetStateAction<boolean>>
}

const ITEM_SIZE = 56
const BUTTON_SIZE = 40
const MAX_LIST_HEIGHT = 300

type OptionTuple = [
  React.HTMLAttributes<HTMLLIElement> & { key: React.Key },
  IStudentCandidate,
]

type VirtualizedListBoxProps = {
  onAddNewStudentClick: () => void
  addNewLabel: string
  scrollOffsetRef: React.MutableRefObject<number>
  outerDivRef: React.MutableRefObject<HTMLDivElement | null>
} & React.HTMLAttributes<HTMLDivElement>

const VirtualizedListBox = React.forwardRef<
  HTMLDivElement,
  VirtualizedListBoxProps
>(function VirtualizedListBox(
  {
    onAddNewStudentClick,
    addNewLabel,
    scrollOffsetRef,
    outerDivRef,
    children,
    ...other
  },
  ref,
) {
  const { ownerState: _ownerState, ...domProps } = other as Record<
    string,
    unknown
  >

  const items = children as unknown as OptionTuple[]
  const itemCount = Array.isArray(items) ? items.length : 0

  const listHeight = useMemo(
    () => Math.min(itemCount * ITEM_SIZE + BUTTON_SIZE, MAX_LIST_HEIGHT),
    [itemCount],
  )

  const handleScroll = useCallback(
    ({ scrollOffset }: { scrollOffset: number }) => {
      scrollOffsetRef.current = scrollOffset
    },
    [scrollOffsetRef],
  )

  const renderRow = useCallback(
    ({ index, style, data }: ListChildComponentProps<OptionTuple[]>) => {
      if (!data || index === data.length) {
        return (
          <div style={style}>
            <Box
              width="100%"
              bgcolor="mockup.primary95"
              display="flex"
              justifyContent="center"
              py="4px"
            >
              <Button variant="text" onClick={onAddNewStudentClick}>
                {addNewLabel}
              </Button>
            </Box>
          </div>
        )
      }

      const [optionProps, option] = data[index]
      const { key, ...htmlProps } = optionProps

      return (
        <li
          key={key}
          {...htmlProps}
          style={{
            ...style,
            cursor: "pointer",
            listStyle: "none",
            height: ITEM_SIZE,
            boxSizing: "border-box",
            display: "flex",
            alignItems: "center",
            padding: "8px 16px",
          }}
        >
          <Stack>
            <Typography>{`${option.first_name} ${option.last_name}`}</Typography>
            <Typography
              sx={(theme) => ({
                color: theme.palette.mockup.neutral10Opacity,
                fontWeight: 600,
                overflowWrap: "break-word",
              })}
            >
              {option.login}
            </Typography>
          </Stack>
        </li>
      )
    },
    [onAddNewStudentClick, addNewLabel],
  )

  return (
    <Box
      ref={ref}
      {...(domProps as React.HTMLAttributes<HTMLDivElement>)}
      // "& > div" targets FixedSizeList's outer scroll container (its direct child div)
      sx={{ "& > div": scrollbarStyles }}
    >
      <FixedSizeList
        height={listHeight}
        width="100%"
        itemSize={ITEM_SIZE}
        itemCount={itemCount + 1}
        itemData={items}
        onScroll={handleScroll}
        initialScrollOffset={scrollOffsetRef.current}
        outerRef={outerDivRef}
      >
        {renderRow}
      </FixedSizeList>
    </Box>
  )
})

const StudentsSearch: FC<IProps> = ({
  onChange,
  onAddNewStudentClick,
  isLoading,
  isError,
  options,
  setIsChange,
}) => {
  const { t } = useTranslation()
  const [info, setInfo] = useState("")
  const [isOpen, setIsOpen] = useState(false)
  const scrollOffsetRef = useRef(0)
  const outerDivRef = useRef<HTMLDivElement | null>(null)

  const sortedOptions = useMemo(
    () => [...options].sort((a, b) => a.first_name.localeCompare(b.first_name)),
    [options],
  )

  const filteredOptions = useMemo(() => {
    if (!info.length) return sortedOptions
    const lower = info.toLowerCase()
    return sortedOptions.filter(
      (el) =>
        el.login.toLowerCase().includes(lower) ||
        `${el.first_name} ${el.last_name}`.toLowerCase().includes(lower),
    )
  }, [sortedOptions, info])

  const handleAddNew = useCallback(() => {
    scrollOffsetRef.current = 0
    setIsOpen(false)
    onAddNewStudentClick()
  }, [onAddNewStudentClick])

  const handleClose = useCallback(() => {
    setIsOpen(false)
    scrollOffsetRef.current = 0
  }, [])

  const addNewLabel = t("students.addNewStudent")

  const ListboxComponent = useMemo(
    () =>
      React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
        function VirtualizedListBoxWrapper(listboxProps, ref) {
          return (
            <VirtualizedListBox
              ref={ref}
              {...listboxProps}
              scrollOffsetRef={scrollOffsetRef}
              outerDivRef={outerDivRef}
              onAddNewStudentClick={handleAddNew}
              addNewLabel={addNewLabel}
            />
          )
        },
      ),
    // refs are stable objects — intentionally omitted from deps
    [handleAddNew, addNewLabel],
  )

  return (
    <Autocomplete
      open={isOpen}
      onOpen={() => setIsOpen(true)}
      onClose={handleClose}
      disableCloseOnSelect
      inputValue={info}
      value={null}
      sx={{ mb: "48px" }}
      noOptionsText={
        <Box display="flex" flexDirection="column" mx="-16px" my="-14px">
          <Box px="16px" py="14px">
            {t("noOptions")}
          </Box>
          <Box
            bgcolor="mockup.primary95"
            display="flex"
            justifyContent="center"
            py="4px"
          >
            <Button
              variant="text"
              onMouseDown={(e) => {
                e.preventDefault()
                handleAddNew()
              }}
            >
              {addNewLabel}
            </Button>
          </Box>
        </Box>
      }
      loadingText={t("loading")}
      loading={isLoading}
      options={filteredOptions}
      onChange={(_event, value) => {
        onChange(value)
      }}
      filterOptions={(opts) => opts}
      getOptionLabel={(option) => `${option.first_name} ${option.last_name}`}
      isOptionEqualToValue={(option, value) => option.login === value.login}
      onInputChange={(event, v, reason) => {
        if (reason !== "input") return
        event?.type === "change" ? setIsChange(true) : setIsChange(false)
        scrollOffsetRef.current = 0
        outerDivRef.current?.scrollTo({ top: 0 })
        setInfo(v)
      }}
      renderOption={(props, option) => [props, option] as React.ReactNode}
      slots={{ listbox: ListboxComponent }}
      renderInput={(params) => (
        <TextFieldMui
          {...params}
          error={isError}
          helperText={isError && t("validations.anErrorOccured")}
          label={`${t("students.searchForStudentNameOrAddNew")}...`}
        />
      )}
    />
  )
}

export default StudentsSearch
